import type { Participant, ShareKind, SfuPublication, SignalPayload } from '@shared/protocol'
import type { PeerConnectionState } from '@/lib/mesh'
import { smoothPlayout } from '@/lib/receiveQuality'
import { WATCH_REANNOUNCE, parseWatchKey } from '@/lib/relayPlan'
import { degradationFor } from '@/lib/screenCapture'
import { waitForIceGathering, type SfuApi, type SfuTrackRef, type SfuTracksResponse } from '@/lib/sfuClient'
import { sampleConnection, type TrackSample } from '@/lib/streamQuality'
import type { LocalStream, StreamTransport } from '@/lib/transport'
import { DEFAULT_ENCODER, preferCodec, type ShareEncoder } from '@/lib/videoEncoder'

export interface SfuTransportOptions {
    api: SfuApi
    iceServers: RTCIceServer[]
    /** O `streamId` identifica QUAL transmissao daquele peer chegou. */
    onStream: (peerId: string, streamId: string, stream: MediaStream) => void
    onPeerGone: (peerId: string) => void
    /**
     * Mesmo contrato do mesh: como esta a conexao com aquele peer. Sem isso o
     * caminho SFU seria ainda menos observavel que a malha — nem `failed`
     * chegaria na UI.
     */
    onPeerState?: (peerId: string, state: PeerConnectionState) => void
    /**
     * Id do proprio participante. So serve pra reportar a saude das MINHAS
     * publicacoes pelo mesmo canal `onPeerState`: quem transmite tambem
     * precisa ver quando a propria publicacao cai. Sem ele o transporte segue
     * republicando a publicacao morta (a parte que corrige a tela preta),
     * apenas sem avisar a UI.
     */
    selfId?: string
    /** Injetavel pra teste. */
    createConnection?: (config: RTCConfiguration) => RTCPeerConnection
    /** Publish video only while someone watches, subscribe only to what I watch; see docs/assistir-sob-demanda.md. */
    onDemand?: boolean
    /** Carries `watch` signals to the publisher. */
    sendSignal?: (to: string, data: SignalPayload) => void
    /** Who to ask for a new `setLocalStreams` when the watchers change what should be published. */
    onRepublish?: () => void
    onPublishError?: (kind: ShareKind, error: unknown) => void
    onWatchers?: (counts: Map<ShareKind, number>) => void
}

/** Keeps a share published this long after the last viewer leaves, so a quick re-click stays instant. */
const PUBLISH_LINGER = 20_000
const FIRST_PACKET_TIMEOUT = 5_000

interface Subscription {
    pc: RTCPeerConnection
    sessionId: string
    /**
     * Mids das trilhas assinadas, pra fechar a sessao quando a assinatura cai.
     * Sem fechar, cada sessao de assistir ficava contando na cota do servidor
     * (`MAX_SFU_SESSIONS`) ate a pessoa sair da sala.
     */
    mids: string[]
    /** Chave do que esta assinado, pra detectar quando o publisher trocou. */
    source: string
    /** Como esta ESTA assinatura; o estado do peer e o agregado das dele. */
    state: PeerConnectionState
}

interface Publication {
    pc: RTCPeerConnection
    sessionId: string
    /** Mid e nome de cada trilha publicada: e o que o fechamento manda. */
    trackRefs: SfuTrackRef[]
    senders: RTCRtpSender[]
    /**
     * Stream e trilhas que ORIGINARAM esta publicacao. E a comparacao que evita
     * republicar a tela toda vez que o microfone liga.
     */
    stream: MediaStream
    tracks: MediaStreamTrack[]
    /** Coordenadas ja anunciadas pra sala; reaproveitadas quando nada mudou. */
    announced: SfuPublication
    /** Encoder com que o video desta publicacao foi negociado. */
    encoder: ShareEncoder
}

/** Assinatura que falhou e vai ser tentada de novo. */
interface PendingRetry {
    peerId: string
    streamId: string
    publication: SfuPublication
    attempts: number
    timer?: ReturnType<typeof setTimeout>
}

/**
 * Teto de RETENTATIVAS de assinatura (a primeira tentativa nao conta). Sem
 * isso, um erro da Cloudflare deixava a assinatura morta ate alguem entrar,
 * sair ou publicar — numa sala estavel de tres pessoas assistindo, isso e tela
 * preta permanente.
 */
const MAX_SUBSCRIBE_RETRIES = 4
const SUBSCRIBE_RETRY_BASE_DELAY = 1_000
const SUBSCRIBE_RETRY_MAX_DELAY = 15_000

/**
 * Nome das trilhas por tipo. Precisa distinguir tela de camera: os dois
 * chegam na mesma conta da Cloudflare, e nomes iguais colidiriam.
 */
const TRACK_NAMES: Record<ShareKind, { video: string; audio: string }> = {
    screen: { video: 'screen-video', audio: 'screen-audio' },
    camera: { video: 'camera-video', audio: 'camera-audio' },
    // Voz nao tem video, mas o mapa cobre os dois pra nao precisar de excecao.
    voice: { video: 'voice-video', audio: 'voice-audio' }
}

/**
 * Camadas de simulcast publicadas para cada trilha de VIDEO.
 *
 * Simulcast e o publicador codificar a mesma imagem em varias qualidades ao
 * mesmo tempo e deixar o SFU escolher qual entregar a cada espectador. Sem
 * ele, todo mundo recebe a mesma unica camada: quem esta com a tela numa
 * miniatura de 160 px baixa e decodifica os mesmos 1080p de quem a colocou em
 * tela cheia, e quem esta numa rede ruim nao tem nada pior pra receber — ou
 * cabe o fluxo inteiro, ou a imagem trava.
 *
 * Os nomes sao de uma letra e ordenados alfabeticamente de proposito: e assim
 * que o `priorityOrdering: 'asciibetical'` da Cloudflare le a escala (`f` de
 * full, `h` de half, `q` de quarter — a mais alta primeiro).
 */
const SIMULCAST_LAYERS: RTCRtpEncodingParameters[] = [
    { rid: 'f', scaleResolutionDownBy: 1 },
    { rid: 'h', scaleResolutionDownBy: 2 },
    { rid: 'q', scaleResolutionDownBy: 4 }
]

/**
 * So a camera vai em camadas. A tela vai inteira, numa camada so.
 *
 * Na tela o simulcast custava justamente o que ela precisa. O Chrome monta um
 * encoder por camada, e a de 480x270 e pequena demais pra placa de video: cai
 * no OpenH264, no processador. Num teste com RTX 5070, a camada cheia de
 * 1080p saiu a 32-40 fps em vez dos 55-57 da camada unica, e a pequena a 8.
 * Na maquina de quem joga enquanto transmite, a tela chegava a 2 fps. E as
 * camadas de cima so ligam quando a banda de subida comporta: ate la, ou
 * quando ela aperta, todo mundo recebia a de 270p a menos de 300 kbps.
 *
 * Sem camadas, quem tem a rede ruim nao tem uma imagem menor pra receber. E a
 * troca certa aqui: a tela e o que a sala inteira esta olhando, e a ideia e
 * todo mundo ver a mesma imagem, cheia.
 */
const SIMULCAST_KINDS: ReadonlySet<ShareKind> = new Set(['camera'])

/** Camada preferida ao assinar: a melhor, com o SFU livre pra descer. */
const PREFERRED_RID = 'f'

/** Uma assinatura por transmissao: a chave junta peer e tipo. */
function subscriptionKey(peerId: string, kind: ShareKind): string {
    return `${peerId}:${kind}`
}

/**
 * Modo SFU: em vez de uma conexao por par, cada pessoa mantem **uma** conexao
 * de publicacao com o Cloudflare Realtime e uma conexao de assinatura por
 * transmissor. O upload de quem transmite deixa de crescer com o tamanho da
 * sala — que e o teto da malha P2P.
 */
export class SfuTransport implements StreamTransport {
    private readonly options: SfuTransportOptions
    private readonly subscriptions = new Map<string, Subscription>()
    private readonly publications = new Map<ShareKind, Publication>()
    /** Assinaturas que falharam e estao no backoff, por chave. */
    private readonly retries = new Map<string, PendingRetry>()
    /** Peers ja vistos, pra avisar quando um some da sala inteira. */
    private readonly knownPeers = new Set<string>()
    /** Ultimo estado avisado por peer — evita repetir o mesmo aviso. */
    private readonly reportedStates = new Map<string, PeerConnectionState>()
    private maxBitrate = 0
    /** Quem codifica o video que sai daqui: placa de video ou processador. */
    private encoder: ShareEncoder = DEFAULT_ENCODER
    private closed = false
    /**
     * O SFU recusou uma publicacao com simulcast: para de tentar pelo resto da
     * sessao. Sem esta memoria, cada nova publicacao pagaria de novo o
     * round-trip que ja se sabe que vai falhar.
     */
    private simulcastDisabled = false
    private lastPeers: Participant[] = []
    private lastStreams: LocalStream[] = []
    /** Viewer side: the `peerId:kind` shares I want. */
    private watched: ReadonlySet<string> = new Set()
    private watchTimer?: ReturnType<typeof setInterval>
    /** Publisher side: who asked for each of my shares. */
    private readonly watchers = new Map<ShareKind, Set<string>>()
    private readonly linger = new Map<ShareKind, ReturnType<typeof setTimeout>>()
    private lastWatcherCounts = ''
    /** Cap the person chose for the whole upload; `0` = none. */
    private uploadLimit = 0

    constructor(options: SfuTransportOptions) {
        this.options = options
    }

    setWatched(keys: ReadonlySet<string>): void {
        const before = this.watched
        this.watched = new Set(keys)
        for (const key of keys) if (!before.has(key)) this.sendWatch(key, true)
        for (const key of before) if (!keys.has(key)) this.sendWatch(key, false)
        if (keys.size > 0 && !this.watchTimer && !this.closed) {
            this.watchTimer = setInterval(() => {
                for (const key of this.watched) this.sendWatch(key, true)
            }, WATCH_REANNOUNCE)
        } else if (keys.size === 0 && this.watchTimer) {
            clearInterval(this.watchTimer)
            this.watchTimer = undefined
        }
        this.syncPeers(this.lastPeers)
    }

    setUploadLimit(bitrate: number): void {
        this.uploadLimit = bitrate
        this.reapplyBitrates()
    }

    private reapplyBitrates() {
        for (const publication of this.publications.values()) {
            for (const sender of publication.senders) void this.applyBitrate(sender)
        }
    }

    private sendWatch(key: string, watching: boolean) {
        const parsed = parseWatchKey(key)
        if (parsed) this.options.sendSignal?.(parsed.peerId, { kind: 'watch', share: parsed.kind, watching })
    }

    private shouldPublish(kind: ShareKind): boolean {
        if (!this.options.onDemand || kind === 'voice') return true
        return (this.watchers.get(kind)?.size ?? 0) > 0 || this.linger.has(kind)
    }

    private watchersChanged(kind: ShareKind) {
        if (!this.options.onDemand || this.closed) return
        const watching = (this.watchers.get(kind)?.size ?? 0) > 0
        const timer = this.linger.get(kind)
        if (watching) {
            if (timer) clearTimeout(timer)
            this.linger.delete(kind)
            if (!this.publications.has(kind) && this.lastStreams.some((local) => local.kind === kind)) {
                this.options.onRepublish?.()
            }
        } else if (!timer && this.publications.has(kind)) {
            this.linger.set(kind, setTimeout(() => {
                this.linger.delete(kind)
                this.options.onRepublish?.()
            }, PUBLISH_LINGER))
        }
        this.reportWatchers()
    }

    private reportWatchers() {
        const counts = new Map<ShareKind, number>()
        for (const local of this.lastStreams) {
            if (local.kind !== 'voice') counts.set(local.kind, this.watchers.get(local.kind)?.size ?? 0)
        }
        const key = JSON.stringify([...counts].sort())
        if (key === this.lastWatcherCounts) return
        this.lastWatcherCounts = key
        this.options.onWatchers?.(counts)
    }

    syncPeers(peers: Participant[]): void {
        this.lastPeers = peers
        const present = new Set(peers.map((peer) => peer.id))
        for (const [kind, ids] of this.watchers) {
            const before = ids.size
            for (const id of [...ids]) if (!present.has(id)) ids.delete(id)
            if (ids.size !== before) this.watchersChanged(kind)
        }
        // Cada publicacao vira uma assinatura propria: tela e camera da mesma
        // pessoa sao duas conexoes, e uma pode cair sem levar a outra.
        const wanted = new Map<string, { peerId: string; publication: SfuPublication; streamId: string }>()
        for (const peer of peers) {
            for (const publication of peer.publications) {
                if (!publication.sfu) continue
                if (this.options.onDemand && publication.kind !== 'voice'
                    && !this.watched.has(subscriptionKey(peer.id, publication.kind))) continue
                wanted.set(subscriptionKey(peer.id, publication.kind), {
                    peerId: peer.id,
                    publication: publication.sfu,
                    streamId: publication.streamId ?? subscriptionKey(peer.id, publication.kind)
                })
            }
        }

        for (const [key, subscription] of [...this.subscriptions]) {
            const target = wanted.get(key)
            // Saiu, parou aquela transmissao ou republicou com outra sessao.
            if (!target || sourceKey(target.publication) !== subscription.source) {
                this.dropSubscription(key)
            }
        }
        // A assinatura que falhou nao esta em `subscriptions`, esta no backoff:
        // sem isto, parar de transmitir (ou sair da sala) no meio das
        // tentativas deixava o espectador batendo numa sessao que ja morreu.
        for (const [key, retry] of [...this.retries]) {
            if (wanted.has(key)) continue
            this.cancelRetry(key)
            this.refreshPeerState(retry.peerId)
        }

        for (const [key, target] of wanted) {
            if (this.subscriptions.has(key)) continue
            const retry = this.retries.get(key)
            if (retry) {
                // Ja ha uma tentativa no backoff. Se o publicador continua o
                // mesmo, deixa o retry seguir; se trocou, recomeca do zero.
                if (sourceKey(retry.publication) === sourceKey(target.publication)) continue
                this.cancelRetry(key)
            }
            void this.subscribe(key, target.peerId, target.streamId, target.publication)
        }

        // Quem sumiu da sala leva junto tudo o que publicava.
        for (const peerId of [...this.knownPeers]) {
            if (!present.has(peerId)) {
                this.knownPeers.delete(peerId)
                this.reportedStates.delete(peerId)
                this.options.onPeerGone(peerId)
                this.options.onPeerState?.(peerId, 'closed')
            }
        }
        for (const peer of peers) this.knownPeers.add(peer.id)
    }

    /**
     * Publica so o que MUDOU.
     *
     * Antes, toda mudanca de captura republicava tudo: ligar o microfone com a
     * tela ja compartilhada criava uma sessao nova pra tela e deixava a antiga
     * orfa — `RTCPeerConnection` sem `close()` aqui e sessao viva faturando do
     * lado da Cloudflare. Agora cada tipo e comparado com o que ja esta no ar
     * (mesma stream, mesmas trilhas) e o inalterado nao e tocado: sem sessao
     * nova, sem renegociacao e sem buraco de midia.
     */
    async setLocalStreams(streams: LocalStream[]): Promise<Map<ShareKind, SfuPublication>> {
        const published = new Map<ShareKind, SfuPublication>()
        if (this.closed) return published
        this.lastStreams = streams
        const active = streams.filter((local) => this.shouldPublish(local.kind))
        this.reportWatchers()

        const wanted = new Set(active.map((local) => local.kind))
        // Tipo que sumiu da captura: derruba a publicacao e encerra a sessao.
        for (const kind of [...this.publications.keys()]) {
            if (!wanted.has(kind)) await this.unpublish(kind)
        }

        for (const local of active) {
            const current = this.publications.get(local.kind)
            if (current && sameSource(current, local.stream) && !this.encoderChanged(current)) {
                published.set(local.kind, current.announced)
                continue
            }

            // Mudou de verdade. Fecha a anterior DESTE tipo antes de trocar:
            // e a unica forma de garantir que a sessao velha nao fique viva.
            if (current) await this.unpublish(local.kind)

            try {
                published.set(local.kind, await this.publish(local))
            } catch (error) {
                // Uma falhar nao derruba a outra: quem conseguiu, continua.
                console.warn('[sfu] falha ao publicar', local.kind, error)
                await this.unpublish(local.kind)
                this.options.onPublishError?.(local.kind, error)
            }
        }

        return published
    }

    /**
     * Guarda o encoder pras proximas publicacoes.
     *
     * Nao mexe no que esta no ar: no SFU o codec e o que a Cloudflare repassa
     * pra cada assinante, e trocar por baixo dela arrisca tela preta do outro
     * lado. Quem troca e a proxima `setLocalStreams`, que republica so o video
     * negociado com o encoder antigo.
     */
    setEncoder(encoder: ShareEncoder): void {
        this.encoder = encoder
    }

    // Not wired yet: here it means picking a simulcast layer via tracks/update.
    requestReceiveLimit(): void {}

    /** Media negotiation is all with Cloudflare; peers only tell each other who is watching. */
    handleSignal(from: string, data: SignalPayload): void {
        if (data.kind !== 'watch' || (data.share !== 'screen' && data.share !== 'camera')) return
        const ids = this.watchers.get(data.share) ?? new Set<string>()
        this.watchers.set(data.share, ids)
        if (data.watching === ids.has(from)) return
        if (data.watching) ids.add(from)
        else ids.delete(from)
        this.watchersChanged(data.share)
    }

    setMaxBitrate(bitrate: number): void {
        this.maxBitrate = bitrate
        this.reapplyBitrates()
    }

    /**
     * Medidas de video das assinaturas E das publicacoes.
     *
     * As duas pontas interessam: a assinatura diz o que esta chegando de cada
     * pessoa, a publicacao diz o que esta saindo daqui. Sao conexoes
     * diferentes com a Cloudflare, entao as duas precisam ser lidas.
     */
    async collectStats(): Promise<Map<string, TrackSample>> {
        const samples = new Map<string, TrackSample>()
        const now = Date.now()
        const connections = [
            ...[...this.subscriptions.values()].map((subscription) => subscription.pc),
            ...[...this.publications.values()].map((publication) => publication.pc)
        ]
        await Promise.all(
            // A assinatura ainda em montagem tem `pc` nulo por um instante.
            connections.filter(Boolean).map((pc) => sampleConnection(pc, samples, now))
        )
        return samples
    }

    close(): void {
        this.closed = true
        for (const key of [...this.retries.keys()]) this.cancelRetry(key)
        for (const key of [...this.subscriptions.keys()]) this.dropSubscription(key)
        this.reportedStates.clear()
        if (this.watchTimer) clearInterval(this.watchTimer)
        for (const timer of this.linger.values()) clearTimeout(timer)
        this.linger.clear()
        void this.unpublishAll()
    }

    // ── interno ───────────────────────────────────────────────────────────────

    /**
     * Publica, preferindo simulcast e caindo pra camada unica se ele nao colar.
     *
     * A queda existe porque simulcast depende do SFU do outro lado aceitar as
     * camadas anunciadas no SDP. Falhar nesse ponto sem alternativa seria
     * trocar uma transmissao com qualidade fixa por transmissao nenhuma — e a
     * pessoa veria "nao consegui publicar" por causa de uma otimizacao.
     */
    private async publish(local: LocalStream): Promise<SfuPublication> {
        try {
            return await this.publishAttempt(local, !this.simulcastDisabled)
        } catch (error) {
            if (this.simulcastDisabled || this.closed) throw error
            console.warn('[sfu] publicacao com simulcast falhou; repetindo em camada unica', error)
            this.simulcastDisabled = true
            return await this.publishAttempt(local, false)
        }
    }

    private async publishAttempt(local: LocalStream, simulcast: boolean): Promise<SfuPublication> {
        const session = await this.options.api.createSession()
        const pc = this.createConnection(session.iceServers)
        try {
            return await this.publishOn(pc, session.sessionId, local, simulcast)
        } catch (error) {
            // Sem isso a conexao e a vaga na cota vazariam a cada tentativa que
            // a Cloudflare recusa.
            const mids = transceiverMids(pc)
            pc.close()
            this.release(session.sessionId, mids.map((mid) => ({ mid })))
            throw error
        }
    }

    private async publishOn(
        pc: RTCPeerConnection,
        sessionId: string,
        local: LocalStream,
        simulcast: boolean
    ): Promise<SfuPublication> {
        const { kind, stream } = local
        const names = TRACK_NAMES[kind]
        const requests: Array<{ location: 'local'; mid: string; trackName: string }> = []
        const senders: RTCRtpSender[] = []
        // Fotografia das trilhas no momento da publicacao: e contra ELA que a
        // proxima chamada decide se este tipo mudou.
        const tracks = stream.getTracks()

        for (const track of tracks) {
            // Simulcast so faz sentido em video: audio ja e barato e o Opus
            // nao tem camadas espaciais pra oferecer.
            const encodings = simulcast && SIMULCAST_KINDS.has(kind) && track.kind === 'video'
                ? SIMULCAST_LAYERS
                : undefined
            const transceiver = pc.addTransceiver(track, {
                direction: 'sendonly',
                streams: [stream],
                ...(encodings ? { sendEncodings: encodings.map((layer) => ({ ...layer })) } : {})
            })
            // Antes da offer: e a ordem dela que a Cloudflare segue na answer.
            // Cloudflare prefers constrained baseline and VP8 when offered, and Chrome runs neither on the card.
            if (track.kind === 'video') preferCodec(transceiver, this.encoder, { hardwareOnly: true })
            senders.push(transceiver.sender)
            requests.push({
                location: 'local',
                // O mid so existe depois do setLocalDescription; preenchido abaixo.
                mid: '',
                trackName: track.kind === 'audio' ? names.audio : names.video
            })
        }

        await pc.setLocalDescription(await pc.createOffer())
        await waitForIceGathering(pc)

        // Casa cada pedido com o mid que o navegador atribuiu.
        const transceivers = pc.getTransceivers().filter((item) => item.sender.track)
        transceivers.forEach((transceiver, index) => {
            const request = requests[index]
            if (request) request.mid = transceiver.mid ?? String(index)
        })

        const response = await this.options.api.newTracks(sessionId, {
            sessionDescription: { type: 'offer', sdp: pc.localDescription?.sdp },
            tracks: requests
        })
        assertNoTrackError(response)

        if (response.sessionDescription) await pc.setRemoteDescription(response.sessionDescription)
        await waitForFirstPacket(senders)

        const trackNames = requests.map((request) => request.trackName)
        const announced: SfuPublication = { sessionId, tracks: trackNames }
        const trackRefs = requests.map(({ mid, trackName }) => ({ mid, trackName }))
        const publication: Publication = {
            pc, sessionId, trackRefs, senders, stream, tracks, announced, encoder: this.encoder
        }
        this.publications.set(kind, publication)
        // A pc de PUBLICACAO tambem cai — TURN que some, troca de rede. Sem
        // este evento ela morre calada: a stream e as trilhas continuam as
        // mesmas, o botao segue em "Parar", e a sala inteira fica assinando
        // uma sessao que nao entrega mais nada.
        pc.onconnectionstatechange = () => {
            if (this.publications.get(kind) !== publication) return
            const state = pc.connectionState
            if (state === 'failed' || state === 'closed') {
                console.warn('[sfu] publicacao caiu', { tipo: kind, estado: state, sessao: sessionId })
            }
            this.refreshSelfState()
        }
        for (const sender of senders) void this.applyBitrate(sender)
        this.refreshSelfState()

        return announced
    }

    /**
     * A publicacao saiu com outro encoder? So video conta: a voz nao tem o que
     * codificar em placa de video, e republica-la a toa cortaria a fala.
     */
    private encoderChanged(publication: Publication): boolean {
        return publication.encoder !== this.encoder
            && publication.tracks.some((track) => track.kind === 'video')
    }

    private async unpublish(kind: ShareKind): Promise<void> {
        const publication = this.publications.get(kind)
        if (!publication) return
        this.publications.delete(kind)
        publication.pc.onconnectionstatechange = null
        publication.pc.close()
        this.refreshSelfState()
        try {
            await this.options.api.closeTracks(publication.sessionId, publication.trackRefs)
        } catch {
            // A sessao expira sozinha na Cloudflare; nao vale travar o botao por isso.
        }
    }

    private async unpublishAll(): Promise<void> {
        for (const kind of [...this.publications.keys()]) await this.unpublish(kind)
    }

    private async subscribe(
        key: string,
        peerId: string,
        streamId: string,
        publication: SfuPublication
    ): Promise<void> {
        const source = sourceKey(publication)
        // Marca a vaga antes do await pra dois eventos seguidos nao assinarem duas vezes.
        const placeholder: Subscription = {
            pc: null as unknown as RTCPeerConnection,
            sessionId: '',
            mids: [],
            source,
            state: 'connecting'
        }
        this.subscriptions.set(key, placeholder)
        this.refreshPeerState(peerId)

        // Toda saida que nao vira assinatura no ar fecha o que ja abriu: a
        // sessao conta na cota do servidor desde o `createSession`, e quem
        // desiste no meio (falha, troca de publicador, sala fechando) e que
        // sabe que ela nao vai ser usada.
        let sessionId: string | null = null
        let pc: RTCPeerConnection | null = null
        let mids: string[] = []
        const abandon = () => {
            pc?.close()
            if (sessionId) this.release(sessionId, mids.map((mid) => ({ mid })))
        }

        try {
            const session = await this.options.api.createSession()
            sessionId = session.sessionId
            if (this.closed || this.subscriptions.get(key) !== placeholder) {
                abandon()
                return
            }

            pc = this.createConnection(session.iceServers)
            const stream = new MediaStream()
            pc.ontrack = (event) => {
                stream.addTrack(event.track)
                if (event.track.kind === 'video') smoothPlayout(event.receiver)
                // Repassa o id anunciado pelo publicador, nao o da stream local
                // montada aqui: e ele que casa com a publicacao la do outro lado.
                this.options.onStream(peerId, streamId, stream)
            }

            const response = await this.options.api.newTracks(session.sessionId, {
                tracks: publication.tracks.map((trackName) => ({
                    location: 'remote',
                    sessionId: publication.sessionId,
                    trackName,
                    /**
                     * Comeca na melhor camada e deixa o SFU descer sozinho
                     * quando a rede DESTE espectador nao der conta.
                     *
                     * E o que faz a qualidade passar a ser por espectador: hoje
                     * uma pessoa no 4G derruba a propria imagem inteira porque
                     * a unica coisa que existe pra receber e o fluxo cheio. Com
                     * as camadas publicadas, ela recebe metade ou um quarto e
                     * continua vendo. `asciibetical` e como a Cloudflare le a
                     * ordem dos rids (`f` > `h` > `q`), tanto pra escolher a
                     * proxima sob congestionamento quanto pra achar substituta
                     * quando a preferida nao existe — o caso do publicador que
                     * nao conseguiu simulcast.
                     */
                    ...(trackName.endsWith('-video')
                        ? {
                            simulcast: {
                                preferredRid: PREFERRED_RID,
                                priorityOrdering: 'asciibetical',
                                ridNotAvailable: 'asciibetical'
                            }
                        }
                        : {})
                }))
            })
            // Antes de conferir erro: num sucesso parcial, as trilhas que
            // entraram tambem precisam ser fechadas.
            mids = responseMids(response)
            assertNoTrackError(response)

            if (response.sessionDescription) {
                await pc.setRemoteDescription(response.sessionDescription)
                await pc.setLocalDescription(await pc.createAnswer())
                await waitForIceGathering(pc)
                if (pc.localDescription) {
                    await this.options.api.renegotiate(session.sessionId, pc.localDescription)
                }
            }
            if (mids.length === 0) mids = transceiverMids(pc)

            if (this.closed || this.subscriptions.get(key) !== placeholder) {
                abandon()
                return
            }
            const live: Subscription = {
                pc,
                sessionId: session.sessionId,
                mids,
                source,
                state: toPeerState(pc.connectionState)
            }
            this.subscriptions.set(key, live)
            // Assinatura no ar nao significa midia chegando: se o ICE nao
            // fechar, o tile fica preto. E este evento que conta a verdade.
            const connection = pc
            connection.onconnectionstatechange = () => {
                const atual = this.subscriptions.get(key)
                if (atual !== live) return
                live.state = toPeerState(connection.connectionState)
                this.refreshPeerState(peerId)
            }
            this.cancelRetry(key)
            this.refreshPeerState(peerId)
        } catch (error) {
            // A track still coming up is routine with on-demand publishing; the retry below sorts it out.
            if (!/track not found/i.test(String((error as Error)?.message))) console.warn('[sfu] falha ao assinar', key, error)
            abandon()
            if (this.subscriptions.get(key) === placeholder) this.subscriptions.delete(key)
            this.scheduleRetry(key, peerId, streamId, publication)
        }
    }

    /**
     * Fecha uma sessao que nao vai mais ser usada e devolve a vaga dela.
     * Best-effort: se falhar, a vaga so volta quando a pessoa sai da sala.
     */
    private release(sessionId: string, tracks: SfuTrackRef[]) {
        void this.options.api.closeTracks(sessionId, tracks).catch(() => undefined)
    }

    /**
     * Reagenda a assinatura com backoff.
     *
     * Sem isso o retry so acontecia se `syncPeers` fosse chamado de novo — ou
     * seja, so se alguem entrasse, saisse ou publicasse. Numa sala parada, o
     * primeiro erro da Cloudflare virava tela preta ate o fim da sessao.
     */
    private scheduleRetry(key: string, peerId: string, streamId: string, publication: SfuPublication) {
        if (this.closed) return
        const previous = this.retries.get(key)
        if (previous?.timer) return

        const attempts = (previous?.attempts ?? 0) + 1
        if (attempts > MAX_SUBSCRIBE_RETRIES) {
            console.warn('[sfu] desistindo de assinar', { assinatura: key, tentativas: attempts })
            this.retries.delete(key)
            // Desistiu: a pessoa precisa saber que aquele video nao vem.
            this.markPeerFailed(peerId)
            return
        }

        const delay = Math.min(SUBSCRIBE_RETRY_BASE_DELAY * 2 ** (attempts - 1), SUBSCRIBE_RETRY_MAX_DELAY)
        console.warn('[sfu] reagendando assinatura', {
            assinatura: key,
            tentativa: `${attempts}/${MAX_SUBSCRIBE_RETRIES}`,
            emMs: delay
        })
        const pending: PendingRetry = { peerId, streamId, publication, attempts }
        pending.timer = setTimeout(() => {
            pending.timer = undefined
            if (this.closed || this.retries.get(key) !== pending) return
            if (this.subscriptions.has(key)) return
            void this.subscribe(key, peerId, streamId, publication)
        }, delay)
        this.retries.set(key, pending)
    }

    private cancelRetry(key: string) {
        const pending = this.retries.get(key)
        if (!pending) return
        if (pending.timer) clearTimeout(pending.timer)
        this.retries.delete(key)
    }

    private dropSubscription(key: string) {
        this.cancelRetry(key)
        const subscription = this.subscriptions.get(key)
        if (!subscription) return
        this.subscriptions.delete(key)
        if (subscription.pc) subscription.pc.onconnectionstatechange = null
        subscription.pc?.close()
        // O placeholder ainda nao tem sessao: a assinatura em andamento ve que
        // perdeu a vaga e fecha a dela.
        if (subscription.sessionId) {
            this.release(subscription.sessionId, subscription.mids.map((mid) => ({ mid })))
        }
        this.refreshPeerState(peerId(key))
    }

    /**
     * Estado do peer = pior estado entre as assinaturas dele. Uma tela travada
     * e camera boa continua sendo problema de conexao com aquela pessoa.
     */
    private refreshPeerState(id: string) {
        let pior: PeerConnectionState | null = null
        for (const [key, subscription] of this.subscriptions) {
            if (peerId(key) !== id) continue
            if (subscription.state === 'failed') { pior = 'failed'; break }
            if (subscription.state === 'connected') pior = pior ?? 'connected'
            else pior = pior === 'connected' || pior === null ? 'connecting' : pior
        }
        // Sem assinatura nenhuma nao ha o que reportar: pode ser so alguem que
        // esta na sala sem transmitir. Se ja houve aviso, fecha o assunto pra
        // UI nao guardar um `failed` de uma transmissao que nem existe mais.
        if (!pior) {
            if (this.reportedStates.has(id)) {
                this.reportedStates.delete(id)
                this.options.onPeerState?.(id, 'closed')
            }
            return
        }
        this.report(id, pior)
    }

    /**
     * Estado das MINHAS publicacoes, pelo mesmo canal das assinaturas.
     *
     * Pior estado vence, igual ao agregado por peer: tela morta com camera de
     * pe continua sendo transmissao quebrada. Uma pc de publicacao que aparece
     * `closed` aqui fechou sozinha — as que eu fecho saem do mapa antes.
     */
    private refreshSelfState() {
        const id = this.options.selfId
        // `unpublishAll` roda depois do `close()`: sem esse guarda, o transporte
        // ja fechado ainda cuspiria estado pra UI.
        if (!id || this.closed) return
        let pior: PeerConnectionState | null = null
        for (const publication of this.publications.values()) {
            const state = toPeerState(publication.pc.connectionState)
            if (state === 'failed' || state === 'closed') { pior = 'failed'; break }
            if (state === 'connected') pior = pior ?? 'connected'
            else pior = pior === 'connected' || pior === null ? 'connecting' : pior
        }
        // Sem publicacao nenhuma nao ha o que reportar sobre mim mesmo.
        if (!pior) {
            if (this.reportedStates.has(id)) {
                this.reportedStates.delete(id)
                this.options.onPeerState?.(id, 'closed')
            }
            return
        }
        this.report(id, pior)
    }

    private markPeerFailed(id: string) {
        this.report(id, 'failed')
    }

    private report(id: string, state: PeerConnectionState) {
        if (this.reportedStates.get(id) === state) return
        this.reportedStates.set(id, state)
        this.options.onPeerState?.(id, state)
    }

    private createConnection(iceServers?: RTCIceServer[]) {
        const factory = this.options.createConnection ?? ((config) => new RTCPeerConnection(config))
        return factory({
            // A Cloudflare devolve o proprio STUN na sessao; o resto e reserva.
            iceServers: iceServers ?? this.options.iceServers,
            bundlePolicy: 'max-bundle'
        })
    }

    /**
     * Aplica o orcamento de upload REPARTIDO entre as camadas de simulcast.
     *
     * As camadas sobem juntas no mesmo link, entao o orcamento e a soma delas —
     * repetir o teto cheio em cada uma pediria o triplo da banda pretendida. A
     * divisao acompanha a quantidade de pixels de cada camada (1, 1/4 e 1/16),
     * que e a proporcao em que elas de fato consomem: a camada cheia leva o
     * grosso, e as menores custam quase nada por cima.
     *
     * Sem simulcast a conta se reduz sozinha a uma camada com o orcamento
     * inteiro, que e o comportamento de antes.
     */
    private async applyBitrate(sender: RTCRtpSender) {
        if (!sender.track || sender.track.kind !== 'video') return
        try {
            const params = sender.getParameters()
            if (!params.encodings || params.encodings.length === 0) params.encodings = [{}]

            const weights = params.encodings.map((encoding) => {
                const scale = encoding.scaleResolutionDownBy ?? 1
                return 1 / (scale * scale)
            })
            const total = weights.reduce((sum, weight) => sum + weight, 0) || 1
            // A chosen cap is about the whole upload, so screen and camera split it.
            const videoKinds = [...this.publications.values()].filter((item) => item.tracks.some((track) => track.kind === 'video')).length
            const capped = this.uploadLimit > 0 ? Math.min(this.maxBitrate || Infinity, this.uploadLimit / Math.max(1, videoKinds)) : this.maxBitrate

            params.encodings.forEach((encoding, index) => {
                if (capped > 0) {
                    encoding.maxBitrate = Math.round(capped * ((weights[index] ?? 1) / total))
                } else {
                    delete encoding.maxBitrate
                }
            })
            const degradation = degradationFor(sender.track)
            if (degradation) params.degradationPreference = degradation
            await sender.setParameters(params)
        } catch (error) {
            console.warn('[sfu] nao deu pra ajustar o bitrate', error)
        }
    }
}

/** Announcing before media flows makes every subscriber fail with "Track not found" and wait out a retry. */
async function waitForFirstPacket(senders: RTCRtpSender[]): Promise<void> {
    const sender = senders.find((item) => item.track?.kind === 'video') ?? senders[0]
    if (typeof sender?.getStats !== 'function') return
    const deadline = Date.now() + FIRST_PACKET_TIMEOUT
    while (Date.now() < deadline) {
        const report = await sender.getStats().catch(() => null)
        let sent = 0
        report?.forEach((stat) => {
            const item = stat as { type?: string; bytesSent?: number }
            if (item.type === 'outbound-rtp') sent += item.bytesSent ?? 0
        })
        if (sent > 0) {
            // The packet still has to reach Cloudflare.
            await new Promise((resolve) => setTimeout(resolve, 250))
            return
        }
        await new Promise((resolve) => setTimeout(resolve, 100))
    }
}

function sourceKey(publication: SfuPublication): string {
    return `${publication.sessionId}:${publication.tracks.join(',')}`
}

/** Inverso de `subscriptionKey`: o tipo nunca tem `:`, entao corta no ultimo. */
function peerId(key: string): string {
    const corte = key.lastIndexOf(':')
    return corte === -1 ? key : key.slice(0, corte)
}

/**
 * A publicacao no ar ainda corresponde a essa captura — e ainda esta viva?
 *
 * Identidade de stream e de trilhas nao diz nada sobre a sessao: quando a pc
 * de publicacao morre, o objeto `MediaStream` e as trilhas continuam os
 * mesmos. Sem olhar a saude, toda chamada seguinte de `setLocalStreams`
 * devolveria as coordenadas ja anunciadas e a sala inteira seguiria assinando
 * uma sessao morta. Antes da otimizacao isso se curava por acidente, porque
 * tudo era republicado a cada chamada.
 */
function sameSource(publication: Publication, stream: MediaStream): boolean {
    const state = publication.pc.connectionState
    if (state === 'failed' || state === 'closed') return false
    if (publication.stream !== stream) return false
    const tracks = typeof stream.getTracks === 'function' ? stream.getTracks() : []
    if (tracks.length !== publication.tracks.length) return false
    return tracks.every((track, index) => publication.tracks[index] === track)
}

function toPeerState(state: RTCPeerConnectionState | undefined): PeerConnectionState {
    switch (state) {
        case 'connected': return 'connected'
        // `disconnected` costuma voltar sozinho — nao e desistencia.
        case 'disconnected':
        case 'connecting': return 'connecting'
        case 'failed': return 'failed'
        case 'closed': return 'closed'
        default: return 'new'
    }
}

/** Mids que a Cloudflare atribuiu as trilhas de um `tracks/new`. */
function responseMids(response: SfuTracksResponse): string[] {
    return (response.tracks ?? [])
        .map((track) => track.mid)
        .filter((mid): mid is string => typeof mid === 'string' && mid.length > 0)
}

/** Mids que o navegador ja atribuiu nesta conexao. */
function transceiverMids(pc: RTCPeerConnection): string[] {
    if (typeof pc.getTransceivers !== 'function') return []
    return pc.getTransceivers()
        .map((transceiver) => transceiver.mid)
        .filter((mid): mid is string => typeof mid === 'string' && mid.length > 0)
}

function assertNoTrackError(response: SfuTracksResponse) {
    if (response.errorCode) {
        throw new Error(`${response.errorCode}: ${response.errorDescription ?? ''}`)
    }
    const failed = response.tracks?.find((track) => track.error || track.errorDescription)
    if (failed) throw new Error(failed.errorDescription ?? failed.error ?? 'track error')
}
